// src/utils/apiClient.js
/**
 * Shared HTTP client for all frontend services.
 *
 * Eliminates the copy-pasted getJson() / fetchWithAuth() helpers
 * that existed independently in brandService, vehicleService,
 * reviewService, categoryService, cartService, orderService, etc.
 *
 * All methods:
 *  - Throw an ApiError (with .status and .data) on non-2xx responses
 *  - Parse JSON automatically
 *  - Use APP_CONFIG.api.baseUrl as the base
 *
 * Usage:
 *   import { api } from '@/utils/apiClient';
 *
 *   // Public (no token)
 *   const data = await api.get('/products?page=1');
 *   const data = await api.post('/contact', { name, email });
 *
 *   // Authenticated
 *   const data = await api.authGet('/orders/my', token);
 *   const data = await api.authPost('/orders/place', payload, token);
 *   const data = await api.authPatch('/cart/items/123', { quantity: 2 }, token);
 *   const data = await api.authDelete('/cart/items/123', token);
 */

import APP_CONFIG from '@/config/app.config';

const BASE_URL = APP_CONFIG.api.baseUrl;
const DEFAULT_REQUEST_TIMEOUT_MS = 8000;
const BASE_PATHNAME = (() => {
  try {
    return new URL(BASE_URL).pathname.replace(/\/$/, '');
  } catch {
    return '/api/v1';
  }
})();

function resolveRequestUrl(url) {
  if (typeof window === 'undefined') return url;
  if (!url?.startsWith('http')) return url;

  try {
    const parsed = new URL(url);
    if (parsed.origin === window.location.origin) return url;

    let relativePath = parsed.pathname;
    if (relativePath.startsWith(BASE_PATHNAME)) {
      relativePath = relativePath.slice(BASE_PATHNAME.length);
    }
    relativePath = relativePath.replace(/^\/+/, '');

    return `/api/proxy/${relativePath}${parsed.search}`;
  } catch {
    return url;
  }
}

// ── Error type ───────────────────────────────────────────────────────────────
export class ApiError extends Error {
  constructor(message, status, data = {}) {
    super(message);
    this.name  = 'ApiError';
    this.status = status;
    this.data   = data;
  }
}

// ── Core fetch wrapper ───────────────────────────────────────────────────────
async function request(path, options = {}) {
  const {
    _requiresAuth = false,
    _retriedAuth = false,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
    ...fetchOptions
  } = options;
  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;
  const requestUrl = resolveRequestUrl(url);
  const requestOptions = {
    credentials: fetchOptions.credentials || 'include',
    ...fetchOptions,
  };

  let response;
  const controller = new AbortController();
  const timeoutHandle = setTimeout(() => controller.abort(), timeoutMs);
  try {
    response = await fetch(requestUrl, {
      ...requestOptions,
      signal: requestOptions.signal || controller.signal,
    });
  } catch (networkError) {
    if (networkError?.name === 'AbortError') {
      throw new ApiError('Request timed out: server took too long to respond', 0, {
        url: requestUrl,
        originalUrl: url,
        timeoutMs,
      });
    }
    throw new ApiError('Network error: could not reach server', 0, { url: requestUrl, originalUrl: url });
  } finally {
    clearTimeout(timeoutHandle);
  }

  // Try to parse JSON regardless of status (errors often have a body)
  const responseClone = response.clone();
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const body = isJson
    ? await response.json().catch(async () => {
        const rawText = await responseClone.text().catch(() => '');
        return rawText;
      })
    : await response.text().catch(() => '');
  const parsedBody = (() => {
    const tryParseJsonLike = (input, depth = 0) => {
      if (typeof input !== 'string' || depth > 3) return input;

      const cleaned = input.replace(/^\uFEFF/, '').trim();
      const parseAttempt = (text) => {
        try {
          return JSON.parse(text);
        } catch {
          return null;
        }
      };

      const bare = cleaned.replace(/^[{]+|[}]+$/g, '');
      const candidates = [cleaned, `{${bare}}`];

      for (const candidate of candidates) {
        const parsed = parseAttempt(candidate);
        if (parsed === null) continue;
        if (typeof parsed === 'string' && parsed !== input) {
          const nested = tryParseJsonLike(parsed, depth + 1);
          if (nested !== parsed) return nested;
        }
        return parsed;
      }

      const start = cleaned.search(/[\[{]/);
      const end = Math.max(cleaned.lastIndexOf('}'), cleaned.lastIndexOf(']'));
      if (start >= 0 && end > start) {
        const sliced = cleaned.slice(start, end + 1);
        const parsed = parseAttempt(sliced);
        if (parsed !== null) {
          if (typeof parsed === 'string') {
            const nested = tryParseJsonLike(parsed, depth + 1);
            if (nested !== parsed) return nested;
          }
          return parsed;
        }
      }

      return input;
    };

    return tryParseJsonLike(body);
  })();

  if (!response.ok) {
    const shouldTryRefresh =
      _requiresAuth &&
      !_retriedAuth &&
      response.status === 401;

    if (shouldTryRefresh) {
      try {
        const refreshUrl = resolveRequestUrl(`${BASE_URL}/auth/refresh`);
        const refreshController = new AbortController();
        const refreshTimeout = setTimeout(
          () => refreshController.abort(),
          timeoutMs,
        );
        let refreshResponse;
        try {
          refreshResponse = await fetch(refreshUrl, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
            signal: refreshController.signal,
          });
        } finally {
          clearTimeout(refreshTimeout);
        }
        const refreshBody = await refreshResponse.json().catch(() => ({}));
        const refreshedToken =
          refreshBody?.data?.accessToken ||
          refreshBody?.accessToken ||
          null;

        if (refreshResponse.ok && refreshedToken) {
          const mergedHeaders = new Headers(requestOptions.headers || {});
          mergedHeaders.set('Authorization', `Bearer ${refreshedToken}`);
          return request(path, {
            ...fetchOptions,
            headers: Object.fromEntries(mergedHeaders.entries()),
            _requiresAuth: true,
            _retriedAuth: true,
          });
        }
      } catch {
        // Fall through to original error handling below.
      }
    }

    const nestedMessage =
      typeof parsedBody === 'object'
        ? parsedBody?.message ||
          parsedBody?.error?.message ||
          parsedBody?.data?.message ||
          parsedBody?.errors?.[0]?.message ||
          null
        : null;
    const message =
      nestedMessage ||
      (typeof parsedBody === 'string' ? parsedBody : null) ||
      `Request failed: ${response.status} ${response.statusText}`;
    throw new ApiError(message, response.status, typeof parsedBody === 'object' ? parsedBody : {});
  }

  // Return just the .data payload when present, else the full body
  return typeof parsedBody === 'object' && 'data' in parsedBody ? parsedBody.data : parsedBody;
}

// ── Helper: build auth headers ───────────────────────────────────────────────
function authHeaders(token, extra = {}) {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
}

// ── Public API ───────────────────────────────────────────────────────────────
export const api = {
  // Public (no auth)
  get:  (path, options = {})        => request(path, { ...options, method: 'GET'  }),
  post: (path, body, options = {})  => request(path, {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    body: JSON.stringify(body),
  }),

  // Authenticated
  authGet: (path, token, options = {}) => request(path, {
    ...options,
    method: 'GET',
    headers: authHeaders(token, options.headers),
    _requiresAuth: true,
  }),

  authPost: (path, body, token, options = {}) => request(path, {
    ...options,
    method: 'POST',
    headers: authHeaders(token, options.headers),
    body: JSON.stringify(body),
    _requiresAuth: true,
  }),

  authPatch: (path, body, token, options = {}) => request(path, {
    ...options,
    method: 'PATCH',
    headers: authHeaders(token, options.headers),
    body: JSON.stringify(body),
    _requiresAuth: true,
  }),

  authPut: (path, body, token, options = {}) => request(path, {
    ...options,
    method: 'PUT',
    headers: authHeaders(token, options.headers),
    body: JSON.stringify(body),
    _requiresAuth: true,
  }),

  authDelete: (path, token, options = {}) => request(path, {
    ...options,
    method: 'DELETE',
    headers: authHeaders(token, options.headers),
    _requiresAuth: true,
  }),
};
